import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/en-gb";

dayjs.extend(utc);
dayjs.extend(isoWeek);

const WEEKDAY_LOCALE = "en-gb";

export function sumBucketValues(buckets) {
    return buckets.reduce((acc, bucket) => acc + bucket.value, 0);
}

/** Normalize stats API day bucket keys to YYYY-MM-DD (avoids ISO datetime / timezone mismatches). */
export function dayKeyFromBucketKey(bucketKey) {
    const raw = `${bucketKey}`.trim();
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
        return match[1];
    }
    return dayjs.utc(raw).format("YYYY-MM-DD");
}

/** Fill missing calendar days with zero; map stats API day buckets to YYYY-MM-DD → count. */
export function bucketsToCountByDay(buckets, startDate, endDate) {
    const out = {};
    let cursor = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).startOf("day");
    while (cursor.isSame(end, "day") || cursor.isBefore(end, "day")) {
        out[cursor.format("YYYY-MM-DD")] = 0;
        cursor = cursor.add(1, "day");
    }
    for (const bucket of buckets) {
        const dayKey = dayKeyFromBucketKey(bucket.key);
        if (dayKey in out) {
            out[dayKey] += bucket.value;
        }
    }
    return out;
}

function experienceNameForKey(experienceNameById, key) {
    return experienceNameById.get(String(key))?.trim() ?? "";
}

/**
 * Most played games from /bookings/stats experience play_counts buckets.
 * Skips unnamed experiences (API already omits empty experience names).
 * Per-game ratings omitted until API float-cast + rated-player metric are available.
 */
export function buildGameAggregatesFromStats(playBuckets, experienceNameById) {
    const entries = playBuckets
        .map((bucket) => {
            const name = experienceNameForKey(experienceNameById, bucket.key);
            if (!name) {
                return null;
            }
            return { name, count: bucket.value };
        })
        .filter(Boolean);

    const totalCount = entries.reduce((acc, item) => acc + item.count, 0);
    const rankedByCount = entries
        .toSorted((a, b) => b.count - a.count)
        .map((item) => ({
            ...item,
            percentage: totalCount ? ((item.count / totalCount) * 100).toFixed(1) : "0.0",
        }));

    return { rankedByCount };
}

/** Popularity by weekday from day-subdivision stats buckets (bookings + players). */
export function computeDayPopularityFromDayBuckets(bookingDayBuckets, playerDayBuckets, weekdayLabels) {
    const bookingCounts = Object.fromEntries(weekdayLabels.map((label) => [label, 0]));
    const playerTotals = Object.fromEntries(weekdayLabels.map((label) => [label, 0]));

    for (const bucket of bookingDayBuckets) {
        const label = dayjs.utc(dayKeyFromBucketKey(bucket.key)).locale(WEEKDAY_LOCALE).format("ddd");
        if (label in bookingCounts) {
            bookingCounts[label] += bucket.value;
        }
    }
    for (const bucket of playerDayBuckets) {
        const label = dayjs.utc(dayKeyFromBucketKey(bucket.key)).locale(WEEKDAY_LOCALE).format("ddd");
        if (label in playerTotals) {
            playerTotals[label] += bucket.value;
        }
    }

    return { weekdayLabels, bookingCounts, playerTotals };
}

export function buildExperienceNameById(experiences) {
    const map = new Map();
    for (const experience of experiences) {
        map.set(String(experience.id), experience.name);
    }
    return map;
}

/** Location-level reports (last month, 12-month) need one location, not an "all" aggregate. */
export function isSingleLocationReportScope(locationId, locationCount) {
    return locationId !== "all" || locationCount === 1;
}

/** Location id for single-location reports and band pricing when filter is All but scope is one site. */
export function resolveReportLocationId(locationId, locations = []) {
    if (locationId != null && locationId !== "all") {
        return locationId;
    }
    if (locations.length === 1) {
        return locations[0].id;
    }
    return "all";
}

/**
 * When /bookings/stats day buckets are empty but /bookings/table count matches the range,
 * use the table total for a single-day chart (stats/table join mismatch workaround).
 */
export function reconcileDayCountsWithTableTotal(countByDay, tableTotal) {
    const keys = Object.keys(countByDay).sort();
    const bucketTotal = keys.reduce((sum, key) => sum + countByDay[key], 0);
    const expected = Number(tableTotal) || 0;
    if (bucketTotal === expected || expected === 0 || bucketTotal > 0) {
        return countByDay;
    }
    if (keys.length === 1) {
        return { ...countByDay, [keys[0]]: expected };
    }
    return countByDay;
}

/** KPI cards for the URL-filtered selected period (from /bookings/table stats). */
export function buildSelectedPeriodSummary(tableStats, locationId = "all") {
    const totalBookings = Number(tableStats?.count) || 0;
    const totalPlayers = Number(tableStats?.total_number_of_players) || 0;
    const avgPlayersPerBooking = totalBookings > 0 ? totalPlayers / totalBookings : 0;
    const avgLocationRating = Number(tableStats?.average_location_rating) || 0;
    const avgExperienceRating = Number(tableStats?.average_rating) || 0;
    const singleLocation = locationId !== "all";
    const hasLocationRating = singleLocation && avgLocationRating > 0;
    const hasExperienceRating = !hasLocationRating && avgExperienceRating > 0;

    return {
        totalBookings,
        totalPlayers,
        avgPlayersPerBooking,
        avgLocationRating: hasLocationRating ? avgLocationRating : avgExperienceRating,
        hasLocationRating: hasLocationRating || hasExperienceRating,
        ratingLabel: hasLocationRating ? "Avg Location Rating" : "Avg Rating",
    };
}

/**
 * Per-location rows for the dashboard Locations table, from `subdivision=location` buckets.
 * Joins bookings + players buckets (keyed by location id) with the user's location list
 * (name, country, cube count). Sorted by bookings desc. Rating / not-finished omitted:
 * the stats endpoint carries one statistic per call and has no per-location rating bucket.
 */
export function buildLocationAggregates(bookingBuckets, playerBuckets, locations) {
    const bookingsById = new Map((bookingBuckets || []).map((b) => [String(b.key), b.value]));
    const playersById = new Map((playerBuckets || []).map((b) => [String(b.key), b.value]));

    const rows = [];
    for (const location of locations || []) {
        const id = String(location.id);
        const bookings = bookingsById.get(id) || 0;
        if (bookings === 0 && !(playersById.get(id) > 0)) {
            continue;
        }
        rows.push({
            id: location.id,
            name: location.name,
            country: location.country || "",
            cubes: location.xcubes?.length || 0,
            bookings,
            players: playersById.get(id) || 0,
        });
    }
    return rows.sort((a, b) => b.bookings - a.bookings);
}

/** Parse API month bucket keys without CEST/UTC shifting Jul/Aug/Oct into the previous month. */
export function monthKeyFromBucketKey(bucketKey) {
    const raw = `${bucketKey}`.trim();
    if (!raw) {
        return "";
    }
    if (/^\d{4}-\d{2}$/.test(raw)) {
        return raw;
    }

    const dateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
        const iso = raw.includes("T") ? raw : `${dateMatch[0]}T12:00:00Z`;
        const parsed = dayjs.utc(iso);
        const day = parsed.date();
        if (day === 1) {
            return parsed.format("YYYY-MM");
        }
        // date_trunc month serialized as previous month's 28–31 (e.g. Oct → 2025-09-30)
        if (day >= 28) {
            return parsed.add(1, "month").startOf("month").format("YYYY-MM");
        }
        return parsed.format("YYYY-MM");
    }

    return dayjs.utc(raw).add(12, "hour").format("YYYY-MM");
}

/** Map month buckets (key YYYY-MM-DD from API month truncation) to ordered month keys. */
export function bucketsToMonthlySeries(buckets, monthKeys) {
    const totals = Object.fromEntries(monthKeys.map((key) => [key, 0]));
    for (const bucket of buckets) {
        const monthKey = monthKeyFromBucketKey(bucket.key);
        if (monthKey in totals) {
            totals[monthKey] += bucket.value;
        }
    }
    return monthKeys.map((key) => totals[key]);
}

/** Fill ISO weeks (Monday keys) in range; map stats API week buckets to counts. */
export function bucketsToCountByWeek(buckets, startDate, endDate) {
    const out = {};
    let cursor = dayjs(startDate).startOf("isoWeek");
    const end = dayjs(endDate).startOf("isoWeek");
    while (cursor.isSame(end, "day") || cursor.isBefore(end, "day")) {
        out[cursor.format("YYYY-MM-DD")] = 0;
        cursor = cursor.add(1, "week");
    }
    for (const bucket of buckets) {
        const weekStart = dayjs.utc(dayKeyFromBucketKey(bucket.key)).startOf("isoWeek").format("YYYY-MM-DD");
        if (weekStart in out) {
            out[weekStart] += bucket.value;
        }
    }
    return out;
}

/** Fill calendar months (YYYY-MM keys) in range; map stats API month buckets to counts. */
export function bucketsToCountByMonth(buckets, startDate, endDate) {
    const out = {};
    let cursor = dayjs(startDate).startOf("month");
    const end = dayjs(endDate).startOf("month");
    while (cursor.isSame(end, "month") || cursor.isBefore(end, "month")) {
        out[cursor.format("YYYY-MM")] = 0;
        cursor = cursor.add(1, "month");
    }
    for (const bucket of buckets) {
        const monthKey = monthKeyFromBucketKey(bucket.key);
        if (monthKey in out) {
            out[monthKey] += bucket.value;
        }
    }
    return out;
}

/** Sum numeric series values (panel subtitles must match chart datasets). */
export function sumSeries(values) {
    return (values || []).reduce((acc, value) => acc + (Number(value) || 0), 0);
}

/**
 * Colored change indicator vs a comparison value (matches the prototype's `verschil`):
 * up (▲ green), down (▼ red), equal (white "Same as"), nodata (no comparison base).
 * `previous === 0` means there is nothing to compare against → "nodata".
 */
export function computeChange(current, previous) {
    const now = Number(current) || 0;
    const base = Number(previous) || 0;
    if (base === 0) {
        return { kind: "nodata", percent: 0 };
    }
    const percent = Math.round(((now - base) / base) * 100);
    if (percent === 0) {
        return { kind: "equal", percent: 0 };
    }
    return { kind: percent > 0 ? "up" : "down", percent };
}

/** Map a change kind to the shared `.verschil` modifier class (prototype op/af/gelijk). */
export function changeKindClass(change) {
    const kind = change?.kind;
    if (kind === "up") return "op";
    if (kind === "down") return "af";
    return "gelijk";
}

/** Human label for a change, e.g. "▲ +12% vs previous week" / "No data for …". */
export function changeText(change, label) {
    if (!change || change.kind === "nodata") {
        return `No data for ${label}`;
    }
    if (change.kind === "equal") {
        return `Same as ${label}`;
    }
    const arrow = change.kind === "up" ? "▲ +" : "▼ −";
    return `${arrow}${Math.abs(change.percent)}% vs ${label}`;
}

/** Per-KPI change set for the selected period vs its preceding same-length window. */
export function buildSelectedPeriodComparison(currentStats, previousStats, label) {
    const curBookings = Number(currentStats?.count) || 0;
    const curPlayers = Number(currentStats?.total_number_of_players) || 0;
    const prevBookings = Number(previousStats?.count) || 0;
    const prevPlayers = Number(previousStats?.total_number_of_players) || 0;
    const curAvg = curBookings > 0 ? curPlayers / curBookings : 0;
    const prevAvg = prevBookings > 0 ? prevPlayers / prevBookings : 0;

    return {
        label,
        // Round avg to one decimal (×10) before comparing, like the prototype.
        bookings: computeChange(curBookings, prevBookings),
        players: computeChange(curPlayers, prevPlayers),
        avgPlayersPerBooking: computeChange(Math.round(curAvg * 10), Math.round(prevAvg * 10)),
    };
}